/**
 * Utility functions for handling connectivity check errors
 */

/**
 * Get localized error message for connectivity error code
 * @param {string} errorCode - Error code from backend
 * @param {Object} connectivityResult - Full connectivity result object
 * @param {Function} localizeMessage - Localization function
 * @returns {string} Localized error message
 */
export function getConnectivityErrorMessage(errorCode, connectivityResult, localizeMessage) {
  const backendMessage = connectivityResult?.message;

  switch (errorCode) {
    case 'BASE_URL_NOT_CONFIGURED':
      return localizeMessage('wizard.connectivity.error.baseUrlNotConfigured');
    case 'INVALID_URL_FORMAT':
      return backendMessage || localizeMessage('wizard.connectivity.error.invalidUrlFormat');
    case 'VALIDATION_FAILED':
      return backendMessage || localizeMessage('wizard.connectivity.error.validation');
    case 'SHOP_NOT_FOUND':
      return localizeMessage('wizard.connectivity.error.shopNotFound');
    case 'INVALID_URL':
      return backendMessage || localizeMessage('wizard.connectivity.error.invalidUrl');
    case 'BLOCKED_HOSTNAME':
    case 'SSRF_DETECTED':
      return localizeMessage('wizard.connectivity.error.blockedHostname');
    case 'BLOCKED_IP':
      return localizeMessage('wizard.connectivity.error.blockedIp');
    case 'DNS_RESOLUTION_FAILED':
      return backendMessage || localizeMessage('wizard.connectivity.error.dnsResolution');
    case 'CONNECTION_TIMEOUT':
      return localizeMessage('wizard.connectivity.error.connectionTimeout');
    case 'CONNECTION_REFUSED':
      return localizeMessage('wizard.connectivity.error.connectionRefused');
    case 'AUTH_REQUIRED':
      return localizeMessage('wizard.connectivity.error.authRequired');
    case 'AUTH_FAILED':
      return localizeMessage('wizard.connectivity.error.authFailed');
    case 'HTTP_ERROR': {
      const httpStatus = connectivityResult?.httpStatus;
      return `${localizeMessage('wizard.connectivity.error.httpError')}: ${httpStatus}`;
    }
    case 'NETWORK_ERROR':
      return backendMessage || localizeMessage('wizard.connectivity.error.networkError');
    default:
      return backendMessage || localizeMessage('wizard.connectivity.error.unknown');
  }
}

/**
 * Check if error code should show ngrok help
 * @param {string} errorCode - Error code from backend
 * @returns {boolean} True if ngrok help should be shown
 */
export function shouldShowNgrokHelp(errorCode) {
  return errorCode === 'BLOCKED_HOSTNAME'
    || errorCode === 'SSRF_DETECTED'
    || errorCode === 'BLOCKED_IP'
    || errorCode === 'CONNECTION_TIMEOUT';
}
